'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getApiUrl, setEnvironment } from '@/lib/api-config';

interface QuickAccessHandlerProps {
  environment: 'production' | 'sandbox';
}

/**
 * Remove the quick-access token from the address bar.
 *
 * The token is a reusable credential that stays valid for 30 days. Left in the
 * URL it sits in browser history — anyone later at a shared computer can sign
 * in as this contractor — and is sent as the Referer on the next same-origin
 * navigation. Only `token` is removed: `contractor_id` is not a secret, and
 * keeping it leaves an address that still says which link was opened.
 */
export function scrubTokenFromAddressBar() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    // Nothing to do: the exchange below does not depend on this succeeding.
  }
}

export function QuickAccessHandler({ environment }: QuickAccessHandlerProps) {
  const searchParams = useSearchParams();
  // Read once. The token is scrubbed from the address bar before the exchange,
  // and Next syncs replaceState into useSearchParams, so reading it on every
  // render would find it gone mid-flight and report a broken link.
  const [credentials] = useState(() => ({
    contractorId: searchParams.get('contractor_id'),
    token: searchParams.get('token'),
  }));
  // "Try Again" re-runs the exchange from the credentials held here. A page
  // reload cannot: the token is no longer in the URL to reload with.
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<
    'processing' | 'error' | 'invalid_link'
  >('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleQuickAccess = async () => {
      const { contractorId, token } = credentials;

      if (!contractorId || !token) {
        setStatus('invalid_link');
        setErrorMessage('Invalid access link. Missing required parameters.');
        return;
      }

      scrubTokenFromAddressBar();

      try {
        const apiUrl = getApiUrl();

        // 1. First verify the link WITH the correct environment header
        // Note: We don't set localStorage yet to avoid persisting 'sandbox' if this fails
        // The credentials travel in the body, not the query string: the API's
        // request logs record every URL in full, and this token stays usable
        // for 30 days.
        const response = await fetch(`${apiUrl}/api/v1/auth/quick-access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Ten99Policy-Environment': environment,
          },
          body: JSON.stringify({ contractor_id: contractorId, token }),
        });

        const data = await response.json();

        if (!response.ok) {
          // The API answers an unknown contractor and a bad token the same
          // way, on purpose, so there is no separate "not found" case here.
          if (response.status === 403) {
            setErrorMessage(data.message || 'Access link is invalid or has expired.');
          } else {
            setErrorMessage(data.message || 'Failed to process access request.');
          }
          setStatus('error');
          return;
        }

        if (data.success && data.magic_token) {
          // 2. Link is valid! NOW we can safely update the persistent environment
          setEnvironment(environment);

          // 3. Redirect to magic-link-callback which handles:
          //    - Token verification and redirect_url extraction
          //    - HMAC-signed pre-verified user for secure auth
          //    - Proper session cookie setting
          //    - Navigation to redirect_url from backend
          const callbackPath = environment === 'sandbox'
            ? '/sandbox/api/auth/magic-link-callback'
            : '/api/auth/magic-link-callback';
          window.location.href = `${callbackPath}?token=${encodeURIComponent(data.magic_token)}`;
        } else {
          setErrorMessage('Invalid response from server.');
          setStatus('error');
        }
      } catch (error) {
        console.error(`${environment} quick access error:`, error);
        setErrorMessage('Network error. Please check your connection and try again.');
        setStatus('error');
      }
    };

    handleQuickAccess();
  }, [credentials, environment, attempt]);

  if (status === 'processing') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
          <h2 className="text-xl font-semibold">Verifying your access...</h2>
          <p className="mt-2 text-muted-foreground">Please wait while we log you in</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'invalid_link') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg border border-destructive bg-destructive/10 p-6">
          <div className="mb-4 flex justify-center">
            <svg className="h-12 w-12 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-destructive">
            Access Error ({environment})
          </h2>
          <p className="mb-4 text-center text-muted-foreground">{errorMessage}</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                setStatus('processing');
                setAttempt((n) => n + 1);
              }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              Try Again
            </button>
            <a href={environment === 'sandbox' ? "/sandbox/signin" : "/signin"} className="text-center text-sm text-muted-foreground hover:text-foreground">
              Return to sign in
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
